import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import React from 'react';
import { BarChart3, GitBranch, MessageSquare, Code, Image, Link, Download, Table, FileText } from 'lucide-react';
const TYPE_CONFIG = {
    chart: { icon: BarChart3, label: 'chart', defaultHeight: 200 },
    mermaid: { icon: GitBranch, label: 'diagram', defaultHeight: 150 },
    proposal: { icon: MessageSquare, label: 'options', defaultHeight: 100 },
    code: { icon: Code, label: 'code', defaultHeight: 80 },
    image: { icon: Image, label: 'image', defaultHeight: 150 },
    link: { icon: Link, label: 'link', defaultHeight: 24 },
    expand: { icon: Download, label: 'content', defaultHeight: 100 },
    table: { icon: Table, label: 'table', defaultHeight: 150 },
    markdown: { icon: FileText, label: 'content', defaultHeight: 100 },
    'fusion-fragment': { icon: Code, label: 'fragment', defaultHeight: 150 },
};
/**
 * Placeholder component shown while code blocks are loading.
 * Provides visual feedback and error states with retry capability.
 */
export function CodeBlockPlaceholder({ type, error, onRetry, message, height, }) {
    const config = TYPE_CONFIG[type];
    const displayHeight = height ?? config.defaultHeight;
    const isInline = type === 'link';
    if (error) {
        return (_jsxs("div", { className: `flex items-center gap-2 rounded border border-destructive/30 bg-destructive/5 text-destructive ${isInline ? 'inline-flex px-2 py-1 text-sm' : 'p-4'}`, style: !isInline ? { minHeight: displayHeight } : undefined, children: [_jsxs("span", { className: "text-destructive", children: ["Failed to load ", config.label] }), onRetry && (_jsx("button", { onClick: onRetry, className: "ml-2 rounded bg-destructive/10 px-2 py-1 text-xs hover:bg-destructive/20 transition-colors", children: "Retry" }))] }));
    }
    const IconComponent = config.icon;
    if (isInline) {
        return (_jsxs("span", { className: "inline-flex items-center gap-1 text-muted animate-pulse", children: [_jsx(IconComponent, { className: "h-3 w-3" }), _jsx("span", { children: message || `Loading ${config.label}...` })] }));
    }
    return (_jsxs("div", { className: "flex flex-col items-center justify-center gap-3 rounded border border-border bg-muted/30 animate-pulse", style: { minHeight: displayHeight }, children: [_jsx(IconComponent, { className: "h-8 w-8 text-muted" }), _jsx("span", { className: "text-sm text-muted", children: message || `Loading ${config.label}...` }), _jsx("div", { className: "flex gap-1", children: [...Array(3)].map((_, i) => (_jsx("div", { className: "h-2 w-2 rounded-full bg-muted animate-bounce", style: { animationDelay: `${i * 150}ms` } }, i))) })] }));
}
export class CodeBlockErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }
    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }
    componentDidCatch(error, errorInfo) {
        console.error(`CodeBlock rendering error (${this.props.type}):`, error, errorInfo);
        this.props.onError?.(error);
    }
    handleRetry = () => {
        this.setState({ hasError: false, error: undefined });
    };
    render() {
        if (this.state.hasError) {
            const { type, fallbackCode } = this.props;
            return (_jsxs("div", { className: "space-y-2", children: [_jsx(CodeBlockPlaceholder, { type: type, error: this.state.error?.message || 'Rendering failed', onRetry: this.handleRetry }), fallbackCode && (_jsxs("details", { className: "text-sm", children: [_jsx("summary", { className: "cursor-pointer text-muted hover:text-foreground", children: "Show raw content" }), _jsx("pre", { className: "mt-2 overflow-x-auto rounded bg-muted/50 p-2 text-xs", children: _jsx("code", { children: fallbackCode }) })] }))] }));
        }
        return this.props.children;
    }
}
//# sourceMappingURL=CodeBlockPlaceholder.js.map